import { FaMapMarkerAlt, FaShieldAlt } from "react-icons/fa";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { Card } from "../common/Card";
import { ErrorRetry } from "../../common/ErrorRetry";
import { DailyRevenueShimmer } from "../../common/reportDashBShimmer/DailyRevenueShimmer";
import { formatInsuranceMoney } from "./insuranceReportUtils";

export function InsuranceRankingsSection({
  state,
  topDestinations,
  topPolicies,
}) {
  return (
    <div>
      <span className="mb-4 text-xl text-[#1A1A1A]">Insurance Rankings</span>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6 bg-white rounded-lg shadow-sm border border-gray-100">
          <span className="mb-6 text-xl text-[#1A1A1A]">
            Top Destinations by Revenue
          </span>
          {state.loading ? (
            <DailyRevenueShimmer />
          ) : state.error ? (
            <div className="h-[300px] flex items-center justify-center">
              <ErrorRetry message={state.message} onRetry={state.onRetry} />
            </div>
          ) : topDestinations.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={topDestinations} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis type="number" stroke="#6B7280" />
                <YAxis
                  dataKey="destination"
                  type="category"
                  width={130}
                  stroke="#6B7280"
                  tick={{ fontSize: 12 }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "white",
                    border: "1px solid #E5E7EB",
                    borderRadius: "8px",
                  }}
                  formatter={(value, name) =>
                    name === "Revenue"
                      ? formatInsuranceMoney(value)
                      : Number(value).toLocaleString()
                  }
                />
                <Legend />
                <Bar
                  dataKey="revenue"
                  fill="#1E61DC"
                  radius={[0, 4, 4, 0]}
                  name="Revenue"
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-gray-500">
              No destination ranking data available
            </div>
          )}
        </Card>

        <Card className="p-6 bg-white rounded-lg shadow-sm border border-gray-100">
          <span className="mb-4 text-[#1A1A1A] text-lg">Top Policies</span>
          {state.loading ? (
            <div className="space-y-4 animate-pulse">
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="h-24 rounded-lg bg-gray-100" />
              ))}
            </div>
          ) : state.error ? (
            <div className="h-[300px] flex items-center justify-center">
              <ErrorRetry message={state.message} onRetry={state.onRetry} />
            </div>
          ) : topPolicies.length > 0 ? (
            <div className="space-y-4">
              {topPolicies.map((policy, index) => (
                <div
                  key={`${policy.policyCode || policy.policyType || index}`}
                  className="flex items-center justify-between p-4 bg-[#F8F9FC] rounded-lg"
                >
                  <div className="flex items-center gap-4 flex-1">
                    <div className="w-10 h-10 rounded-lg bg-white border border-gray-200 flex items-center justify-center">
                      <FaShieldAlt className="w-5 h-5 text-[#1E61DC]" />
                    </div>
                    <div>
                      <div className="text-[#1A1A1A]">
                        {policy.policyName ||
                          policy.policyType ||
                          policy.destinationCountry ||
                          "Insurance Policy"}
                      </div>
                      <div className="flex items-center gap-2 text-[#6B7280] flex-wrap">
                        {policy.policies != null ? (
                          <span>{policy.policies} policies</span>
                        ) : null}
                        {policy.policyCode ? <span>Code {policy.policyCode}</span> : null}
                        {policy.destinationCountry ? (
                          <span className="flex items-center gap-1">
                            <FaMapMarkerAlt className="w-3 h-3 text-[#1E61DC]" />
                            {policy.destinationCountry}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[#1A1A1A]">
                      {formatInsuranceMoney(policy.revenue)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-gray-500">
              No policy ranking data available
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
